import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { MaskLine } from "../Reveal";
import { ArrowDownRight } from "lucide-react";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const ySig = useTransform(scrollYProgress, [0, 1], [0, -60]);

  return (
    <section ref={ref} data-testid="hero-section" className="relative min-h-screen overflow-hidden pt-28 md:pt-24">
      {/* pastel blobs */}
      <div className="pointer-events-none absolute -top-32 -left-32 h-[520px] w-[520px] rounded-full bg-mne-pink/40 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 right-0 h-[420px] w-[420px] rounded-full bg-mne-blue/40 blur-[120px]" />

      <div className="relative max-w-[1400px] mx-auto px-6 md:px-10 grid lg:grid-cols-12 gap-10 items-center min-h-[calc(100vh-7rem)]">
        {/* Left copy */}
        <div className="lg:col-span-7 relative z-10">
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.1, duration: 0.8 }}
            className="text-[11px] uppercase tracking-[0.32em] text-gold mb-6"
          >
            Female Lifestyle Consultant · Est. for Women 40+
          </motion.p>

          <h1 data-testid="hero-headline" className="font-serif font-light leading-[0.92] tracking-tight text-plum text-5xl sm:text-6xl md:text-7xl xl:text-8xl">
            <MaskLine delay={0.15}>Feel Strong.</MaskLine>
            <MaskLine delay={0.28} className="italic text-stone">Think Clearly.</MaskLine>
            <MaskLine delay={0.41}>Glow From Within.</MaskLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.9 }}
            className="mt-8 max-w-lg text-base md:text-lg text-stone leading-relaxed"
          >
            Online personal training, life coaching and luxury skincare — helping women feel their
            best in <span className="text-plum">body, mind and skin</span>.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85, duration: 0.9 }}
            className="mt-10 flex flex-wrap items-center gap-4"
          >
            <button
              data-testid="hero-book-cta"
              onClick={() => document.getElementById("contact")?.scrollIntoView({ behavior: "smooth" })}
              className="group inline-flex items-center gap-3 rounded-full bg-plum px-8 py-4 text-[12px] uppercase tracking-[0.18em] text-cream transition-colors duration-300 hover:bg-plum-deep"
            >
              Book a Free Discovery Call
              <ArrowDownRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:translate-y-1" />
            </button>
            <button
              data-testid="hero-formula-cta"
              onClick={() => document.getElementById("formula")?.scrollIntoView({ behavior: "smooth" })}
              className="rounded-full border border-plum/40 px-8 py-4 text-[12px] uppercase tracking-[0.18em] text-plum transition-colors duration-300 hover:bg-plum/5"
            >
              Explore the MNE Formula
            </button>
          </motion.div>
        </div>

        {/* Right portrait */}
        <div data-testid="hero-portrait-column" className="lg:col-span-5 relative z-10 flex flex-col items-center justify-center gap-8">
          <motion.div
            data-testid="hero-portrait"
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 1.05 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.4, duration: 1.4, ease: [0.22, 1, 0.36, 1] }}
            className="relative w-[280px] sm:w-[340px] lg:w-[400px] aspect-square overflow-hidden rounded-full border-[6px] border-cream shadow-[0_30px_80px_-30px_rgba(74,32,64,0.45)] ring-1 ring-border"
          >
            <img src="/assets/jen-profile.png" alt="Jennie-Marie Glover" className="h-full w-full object-cover object-top bg-linen scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-plum/20 via-transparent to-transparent" />
          </motion.div>

          <motion.img
            data-testid="hero-signature"
            style={{ y: ySig }}
            src="/assets/jennie-marie-logo.png"
            alt="Jennie-Marie signature"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1, duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
            className="w-52 md:w-64 opacity-90 drop-shadow-sm"
          />
        </div>
      </div>
    </section>
  );
}
